import React, { useEffect, useRef, useState } from 'react';
import { toastError } from '../../lib/toast';
import { ToolIcon } from '../home/tools/ToolTile';
import './requests.css';

const REASON_MAX = 500;

export default function RequestAccessModal({ tool, onCancel, onSubmit, returnFocusTo }) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const onKey = (e) => {
      // Like the backdrop, Escape never throws away a typed reason; the
      // Cancel button is the explicit way out then.
      if (e.key === 'Escape' && !submitting && reason.trim().length === 0) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel, submitting, reason]);

  // Return focus to the element that opened the modal when it unmounts. The
  // caller passes it (the card's button): document.activeElement is <body> in
  // Safari, which does not focus a button on click. The reason box takes focus
  // via autoFocus.
  const openerRef = useRef(returnFocusTo || (typeof document !== 'undefined' ? document.activeElement : null));
  useEffect(() => () => {
    const el = openerRef.current;
    if (el && typeof el.focus === 'function' && document.contains(el)) el.focus();
  }, []);

  const trimmedLen = reason.trim().length;
  const canSubmit = trimmedLen > 0 && trimmedLen <= REASON_MAX && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await onSubmit(reason.trim());
      // success: parent closes the modal and shows the confirmation toast
    } catch (err) {
      toastError(err.message || "Couldn't send your request. Try again.");
      setSubmitting(false);
    }
  };

  // aria-modal promises the page behind is out of reach, so Tab and Shift+Tab
  // wrap inside the dialog instead of walking into the catalog underneath.
  const dialogRef = useRef(null);
  const trapTab = (e) => {
    if (e.key !== 'Tab' || !dialogRef.current) return;
    const focusables = [...dialogRef.current.querySelectorAll(
      'button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])'
    )];
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  // A stray click on the backdrop must not throw away a typed reason.
  const onOverlayClick = () => {
    if (!submitting && reason.trim().length === 0) onCancel();
  };

  return (
    <div className="modal-overlay" onClick={onOverlayClick}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tar-title"
        ref={dialogRef}
        onKeyDown={trapTab}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 480 }}
      >
        <h2 id="tar-title" style={{ marginTop: 0, fontSize: 18 }}>Request access to “{tool.name}”</h2>

        <div className="form-group">
          <span className="rq-caption">Tool</span>
          <div className="rq-tool-summary">
            <span className="rq-tool-icon"><ToolIcon tool={tool} /></span>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <strong>{tool.name}</strong>
              {tool.category && <span className="rq-sub" style={{ fontSize: 12 }}>{tool.category}</span>}
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="tar-reason">
              Reason for access <span className="rq-required" aria-hidden="true">*</span>
            </label>
            <textarea
              id="tar-reason"
              autoFocus
              required
              aria-describedby="tar-reason-count"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={4}
              placeholder="Tell the admin how you'll use this. Requests without a clear reason may be rejected."
              maxLength={REASON_MAX + 50 /* let user paste, validate on submit */}
              style={{ resize: 'vertical', minHeight: 90 }}
            />
            <div className="rq-field-meta">
              <span />
              <span id="tar-reason-count" className={trimmedLen > REASON_MAX ? 'is-over' : undefined}>
                {trimmedLen} / {REASON_MAX}
              </span>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={!canSubmit}>
              {submitting ? 'Submitting…' : 'Submit request'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
